import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  FaLinkedinIn,
  FaWhatsapp,
  FaEnvelope,
  FaBrain,
  FaCode,
  FaRocket,
  FaCrown,
  FaChartLine,
  FaHandshake,
} from 'react-icons/fa';


const Team = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const allMembers = [
    {
      name: 'Nishant Mishra',
      position: 'Founder and Director',
      bio: 'Founder with a dual degree in Philosophy (BA & MA Hons), applying critical thinking to build efficient CRM systems and support data-driven digital marketing. Works closely with teams to streamline operations, improve customer engagement, and deliver scalable growth solutions for modern businesses.',
      image: './images/Nishant.jpeg',
      icon: FaCrown,
      expertise: ['Strategic Leadership', 'Business Growth'],
      profileUrl: 'https://www.linkedin.com/in/sronzer-team-0081793ab/',
      social: {
        linkedin: 'https://www.linkedin.com/in/sronzer-team-0081793ab?utm_source=share_via&utm_content=profile&utm_medium=member_android',
        whatsapp: 'https://wa.me/919277128460',
        email: 'connect@sronzer.com'
      }
    },
    {
      name: 'Prajwal Singh',
      position: 'Co-Founder',
      bio: 'Salesforce CRM Expert with 6+ years of experience delivering scalable CRM solutions. Passionate about helping businesses optimize processes, improve customer engagement, and achieve growth through technology.',
      image: './images/mohit.png',
      icon: FaRocket,
      expertise: ['CRM-Tech', 'Strategic Leadership', 'Business Development'],
      profileUrl: 'https://www.salesforce.com/trailblazer/prajwalsingh',
      social: {
        linkedin: 'https://www.linkedin.com/in/prajwal-singh-35b24212a/',
        whatsapp: 'https://wa.me/919713628455',
        email: 'connect@sronzer.com'
      }
    },
    {
      name: 'Tuhina Singh Parihar',
      position: 'Legal Advisor and Aid',
      bio: 'Provides legal guidance to support compliance, policy alignment, and risk management across operations—helping the team deliver trustworthy, transparent services.',
      image: './images/Tuhina.jpeg',
      icon: FaHandshake,
      expertise: ['Legal Advisory', 'Compliance', 'Risk Management'],
      profileUrl: '',
      social: {
        linkedin: '#',
        whatsapp: '#',
        email: 'connect@sronzer.com'
      }
    }
  ];

  return (
    <div className="min-h-screen bg-primary-950">
      {/* Hero Section */}
      <section className="relative py-12 md:py-16 overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-5" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-accent/5 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="text-accent font-display text-sm tracking-[0.3em]">OUR LEADERSHIP</span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-white mt-3 mb-3">
              Meet The <span className="text-accent">TEAM</span>
            </h1>
            <p className="text-gray-400 text-base max-w-2xl mx-auto">
              Visionary leaders building the future of CRM solutions
            </p>
            <div className="golden-line mx-auto mt-4" />
          </motion.div>
        </div>
      </section>

      {/* Team Cards Section */}
      <section className="relative pb-12" ref={ref}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-5">
          {allMembers.map((member, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.45, delay: index * 0.12 }}
              whileHover={{ y: -3 }}
              className="glass-card group cursor-default relative overflow-hidden flex items-stretch rounded-xl border border-accent/10 hover:border-accent/30 transition-all duration-300"

            >
              {/* Left: Image - fills entire column from top to bottom */}
              <div className="relative flex-shrink-0 w-36 md:w-44 overflow-hidden rounded-l-xl">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full object-cover object-center absolute inset-0"
                />
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-accent/10 to-transparent z-10 pointer-events-none" />
                {/* Icon badge */}
                <div className="absolute bottom-2 right-2 w-8 h-8 bg-primary-900/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg z-20 border border-accent/40">
                  <member.icon className="text-accent text-sm" />
                </div>
              </div>

              {/* Right: Content */}
              <div className="flex-1 px-5 py-4 flex flex-col justify-between">
                <div>

                  <div className="flex items-start justify-between mb-0.5">
                    <div>
                      <h3 className="text-lg md:text-xl font-display font-bold text-white leading-tight">
                        {member.name.toUpperCase()}
                      </h3>
                      <p className="text-accent font-display font-semibold text-xs tracking-widest uppercase mt-0.5">
                        {member.position}
                      </p>
                    </div>
                  </div>
                  <p className="text-gray-400 text-[11px] md:text-xs leading-relaxed mt-2 mb-3 line-clamp-3">
                    {member.bio}
                  </p>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {member.expertise.map((skill, idx) => (
                      <span
                        key={idx}
                        className="text-[9px] md:text-[10px] bg-primary-800/50 text-gray-300 px-2 py-0.5 rounded-full border border-accent/10"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-3 pt-2 border-t border-gray-800/60">


                  {member.name === 'Prajwal Singh' ? (
                    <a
                      href={member.profileUrl}
                      className="text-gray-500 hover:text-accent transition-all hover:scale-110"
                      onClick={(e) => e.stopPropagation()}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FaRocket size={13} />
                    </a>
                  ) : null}

                  <a
                    href={member.social.linkedin}
                    className="text-gray-500 hover:text-accent transition-all hover:scale-110"
                    onClick={(e) => e.stopPropagation()}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaLinkedinIn size={13} />
                  </a>
                  <a
                    href={member.social.whatsapp}
                    className="text-gray-500 hover:text-green-500 transition-all hover:scale-110"
                    onClick={(e) => e.stopPropagation()}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaWhatsapp size={13} />
                  </a>
                  <a
                    href={`mailto:${member.social.email}`}
                    className="text-gray-500 hover:text-accent transition-all hover:scale-110"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FaEnvelope size={13} />
                  </a>
                </div>
              </div>

              {/* Bottom glow line */}
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-accent to-transparent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
            </motion.div>
          ))}
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.4, delay: 0.4 }}
          className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 grid grid-cols-3 gap-3"
        >
          {[
            { value: '8+', label: 'Years Exp', icon: FaChartLine },
            { value: '50+', label: 'Projects', icon: FaRocket },
            { value: '98%', label: 'Satisfaction', icon: FaBrain },
          ].map((stat, idx) => (
            <div key={idx} className="glass-card p-3 text-center">
              <stat.icon className="text-base text-accent mx-auto mb-1" />
              <div className="text-xl font-display font-bold text-white">{stat.value}</div>
              <div className="text-gray-400 text-[10px]">{stat.label}</div>
            </div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.4, delay: 0.5 }}
          className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-4"
        >
          <div className="glass-card p-3 text-center">
            <FaHandshake className="text-xl text-accent mx-auto mb-1" />
            <p className="text-gray-400 text-[11px]">
              United by a shared vision to revolutionize CRM solutions
            </p>
          </div>
        </motion.div>
      </section>

      {/* Join CTA */}
      <section className="relative py-12 bg-primary-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-3xl font-display font-bold text-white mb-2">
              Want to <span className="text-accent">Join Our Team</span>?
            </h2>
            <p className="text-gray-400 text-sm mb-4 max-w-xl mx-auto">
              We're always looking for talented developers and CRM experts
            </p>
            <button className="btn-primary inline-flex text-sm py-2 px-6">
              VIEW OPEN POSITIONS
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Team;